import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import usePageTitle from '../../hooks/usePageTitle';
import '../../styles/fonts.css';

const MODES = ['load', 'range', 'consumption'];

// shared input style
const inp = {
  background: '#21262d',
  border: '1px solid #30363d',
  borderRadius: '6px',
  color: '#e6edf3',
  padding: '8px 12px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const lbl = {
  fontSize: '12px',
  color: '#8b949e',
  marginBottom: '4px',
  display: 'block',
};

function Field({ label, children, hint }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={lbl}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: '11px', color: '#e3b341' }}>⚠ {hint}</span>}
    </div>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid #21262d',
    }}>
      <span style={{ fontSize: '13px', color: '#8b949e' }}>{label}</span>
      <span style={{ fontSize: highlight ? '22px' : '15px', fontWeight: highlight ? 700 : 600, color: highlight ? '#e3b341' : '#e6edf3' }}>
        {value}
      </span>
    </div>
  );
}

function ModeBtn({ active, color, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 600,
      border: '1px solid', cursor: 'pointer',
      borderColor: active ? color : '#30363d',
      background: active ? `rgba(${hexToRgb(color)},0.12)` : '#21262d',
      color: active ? color : '#8b949e',
      transition: 'all 0.15s',
    }}>
      {children}
    </button>
  );
}

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

function FuelCalculator() {
  const { t } = useTranslation();
  usePageTitle(t('tools.fuel.title'));

  const [mode, setMode] = useState('load');
  const [unit, setUnit] = useState('L');

  // shared inputs
  const [fuelPerLap, setFuelPerLap] = useState('');
  const [laps, setLaps] = useState('');
  const [fuelInTank, setFuelInTank] = useState('');
  const [margin, setMargin] = useState('1');

  // duration submode (load only)
  const [usesDuration, setUsesDuration] = useState(false);
  const [durH, setDurH] = useState('');
  const [durM, setDurM] = useState('');
  const [ltM, setLtM] = useState('');
  const [ltS, setLtS] = useState('');

  const computedLapsFromDuration = useMemo(() => {
    const totalSec = (parseInt(durH || 0) * 3600) + (parseInt(durM || 0) * 60);
    const lapSec = (parseInt(ltM || 0) * 60) + parseFloat(ltS || 0);
    if (totalSec <= 0 || lapSec <= 0) return null;
    return Math.ceil(totalSec / lapSec);
  }, [durH, durM, ltM, ltS]);

  const effectiveLaps = usesDuration && mode === 'load'
    ? computedLapsFromDuration
    : (laps ? parseInt(laps) : null);

  const result = useMemo(() => {
    const fpl = parseFloat(fuelPerLap);
    const fit = parseFloat(fuelInTank);
    const l = effectiveLaps;
    const m = parseInt(margin || 0);

    if (mode === 'load') {
      if (!fpl || fpl <= 0 || !l || l <= 0) return null;
      return {
        base: fpl * l,
        withMargin: fpl * (l + m),
        marginLaps: m,
      };
    }
    if (mode === 'range') {
      if (!fpl || fpl <= 0 || !fit || fit <= 0) return null;
      return { lapsAchievable: Math.floor(fit / fpl) };
    }
    if (mode === 'consumption') {
      if (!fit || fit <= 0 || !l || l <= 0) return null;
      return { maxFpl: fit / l };
    }
    return null;
  }, [mode, fuelPerLap, fuelInTank, effectiveLaps, margin]);

  const modeColor = { load: '#e3b341', range: '#58a6ff', consumption: '#3fb950' };

  return (
    <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <main style={{ flex: 1, maxWidth: '960px', margin: '0 auto', padding: '48px 24px', color: '#c9d1d9' }}>

        <div style={{ marginBottom: '12px' }}>
          <Link to="/tools" style={{ fontSize: '13px', color: '#8b949e', textDecoration: 'none' }}>
            ← {t('tools.pageTitle')}
          </Link>
        </div>
        <h1 style={{ fontSize: '26px', fontWeight: 700, color: '#fff', marginBottom: '6px' }}>
          <i className="fas fa-gas-pump" style={{ color: '#e3b341', marginRight: '10px' }} />
          {t('tools.fuel.title')}
        </h1>
        <p style={{ fontSize: '13px', color: '#8b949e', marginBottom: '32px' }}>
          {t('tools.fuel.desc')}
        </p>

        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start' }}>

          {/* LEFT — inputs */}
          <div style={{ flex: 1, minWidth: 0 }}>

            {/* Mode selector */}
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                {t('tools.fuel.modeLabel')}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {MODES.map(m => (
                  <ModeBtn key={m} active={mode === m} color={modeColor[m]} onClick={() => { setMode(m); setUsesDuration(false); }}>
                    {t(`tools.fuel.mode_${m}`)}
                  </ModeBtn>
                ))}
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#8b949e', marginBottom: '28px', marginTop: '10px' }}>
              {t(`tools.fuel.modeDesc_${mode}`)}
            </p>

            {/* Unit toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '28px' }}>
              <span style={{ fontSize: '13px', color: '#8b949e' }}>{t('tools.fuel.unit')}:</span>
              {['L', 'kg'].map(u => (
                <button key={u} onClick={() => setUnit(u)} style={{
                  padding: '4px 14px', borderRadius: '4px', fontSize: '13px', fontWeight: 600,
                  border: '1px solid', cursor: 'pointer',
                  borderColor: unit === u ? '#58a6ff' : '#30363d',
                  background: unit === u ? 'rgba(88,166,255,0.12)' : 'transparent',
                  color: unit === u ? '#58a6ff' : '#8b949e',
                }}>{u}</button>
              ))}
            </div>

            {/* Inputs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

              {(mode === 'load' || mode === 'range') && (
                <Field
                  label={`${t('tools.fuel.fuelPerLap')} (${unit})`}
                  hint={parseFloat(fuelPerLap) > 10 ? t('tools.fuel.highConsumptionWarning') : null}
                >
                  <input
                    type="number" min="0" step="0.01"
                    value={fuelPerLap} onChange={e => setFuelPerLap(e.target.value)}
                    placeholder="ex: 2.35"
                    style={{ ...inp, width: '160px' }}
                  />
                </Field>
              )}

              {(mode === 'range' || mode === 'consumption') && (
                <Field label={`${t('tools.fuel.fuelInTank')} (${unit})`}>
                  <input
                    type="number" min="0" step="0.1"
                    value={fuelInTank} onChange={e => setFuelInTank(e.target.value)}
                    placeholder="ex: 40"
                    style={{ ...inp, width: '160px' }}
                  />
                </Field>
              )}

              {(mode === 'load' || mode === 'consumption') && (
                <>
                  {mode === 'load' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox" id="chk-duration"
                        checked={usesDuration} onChange={e => setUsesDuration(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="chk-duration" style={{ fontSize: '13px', color: '#c9d1d9', cursor: 'pointer' }}>
                        {t('tools.fuel.byDuration')}
                      </label>
                    </div>
                  )}

                  {mode === 'load' && usesDuration ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', paddingLeft: '12px', borderLeft: '2px solid #30363d' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                        <Field label={t('tools.fuel.durationH')}>
                          <input type="number" min="0" value={durH} onChange={e => setDurH(e.target.value)} placeholder="0" style={{ ...inp, width: '72px' }} />
                        </Field>
                        <Field label={t('tools.fuel.durationM')}>
                          <input type="number" min="0" max="59" value={durM} onChange={e => setDurM(e.target.value)} placeholder="30" style={{ ...inp, width: '72px' }} />
                        </Field>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                        <Field label={t('tools.fuel.lapTimeM')}>
                          <input type="number" min="0" value={ltM} onChange={e => setLtM(e.target.value)} placeholder="1" style={{ ...inp, width: '72px' }} />
                        </Field>
                        <Field label={t('tools.fuel.lapTimeS')}>
                          <input type="number" min="0" max="59" step="0.1" value={ltS} onChange={e => setLtS(e.target.value)} placeholder="45.0" style={{ ...inp, width: '90px' }} />
                        </Field>
                      </div>
                      {computedLapsFromDuration && (
                        <div style={{ fontSize: '13px', color: '#8b949e' }}>
                          ≈ <strong style={{ color: '#e6edf3' }}>{computedLapsFromDuration}</strong> {t('tools.fuel.laps')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Field label={t('tools.fuel.lapsField')}>
                      <input
                        type="number" min="1"
                        value={laps} onChange={e => setLaps(e.target.value)}
                        placeholder="ex: 30"
                        style={{ ...inp, width: '160px' }}
                      />
                    </Field>
                  )}
                </>
              )}

              {mode === 'load' && (
                <Field label={`${t('tools.fuel.margin')} (${t('tools.fuel.laps')})`}>
                  <input
                    type="number" min="0" max="10"
                    value={margin} onChange={e => setMargin(e.target.value)}
                    style={{ ...inp, width: '80px' }}
                  />
                </Field>
              )}
            </div>
          </div>

          {/* RIGHT — result card (always visible) */}
          <div style={{ width: '260px', flexShrink: 0, position: 'sticky', top: '24px' }}>
            <div className="tools-result-card" style={{
              background: '#161b22',
              border: '1px solid #30363d',
              borderRadius: '10px',
              padding: '24px',
            }}>
              <div style={{ fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '14px' }}>
                {t('tools.fuel.result')}
              </div>

              {result ? (
                <>
                  {mode === 'load' && (
                    <>
                      <ResultRow label={t('tools.fuel.baseAmount')} value={`${result.base.toFixed(2)} ${unit}`} />
                      <ResultRow
                        label={`${t('tools.fuel.withMargin')} (+${margin} ${t('tools.fuel.laps')})`}
                        value={`${result.withMargin.toFixed(2)} ${unit}`}
                        highlight
                      />
                    </>
                  )}
                  {mode === 'range' && (
                    <ResultRow label={t('tools.fuel.lapsAchievable')} value={`${result.lapsAchievable} ${t('tools.fuel.laps')}`} highlight />
                  )}
                  {mode === 'consumption' && (
                    <ResultRow label={t('tools.fuel.maxFuelPerLap')} value={`${result.maxFpl.toFixed(3)} ${unit}/${t('tools.fuel.lapShort')}`} highlight />
                  )}
                </>
              ) : (
                <div style={{ color: '#8b949e', fontSize: '13px', textAlign: 'center', padding: '24px 0', lineHeight: 1.6 }}>
                  {t('tools.fuel.resultPlaceholder')}
                </div>
              )}
            </div>
          </div>

        </div>

      </main>
      <Footer />
    </div>
  );
}

export default FuelCalculator;
